import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BASE_URL } from "../lib/api";
import useSeo, { buildBreadcrumb } from "../lib/useSeo";
import { ArrowLeft, ArrowUpRight, TrendingUp, ExternalLink, Sparkles } from "lucide-react";

// Resolve image paths — support both absolute URLs and backend-relative paths
// like "/api/static/skaira/hero-earth.jpg".
const resolveImg = (src) => {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  return `${BASE_URL}${src}`;
};

export function CasosList() {
  const [projects, setProjects] = useState([]);
  useSeo({
    title: "Casos de éxito · Software a medida desarrollado por DoSoft",
    description: "Conoce proyectos reales que hemos desarrollado: PaySync, Velify, AM Insight, SIGSAOP y NextPlan. Software a medida para finanzas, transporte, auditoría, sostenibilidad y operaciones.",
    canonical: "https://dosoft.cl/casos",
    jsonLd: [
      buildBreadcrumb([
        { name: "Inicio", url: "/" },
        { name: "Casos de éxito", url: "/casos" },
      ]),
    ],
  });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="casos-page">
      <section className="border-b border-ink-500 py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6">
            Casos de éxito
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter text-ash-100 max-w-4xl leading-[0.95]">
            Historias reales,<br />resultados medibles.
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/casos/${p.slug}`}
                data-testid={`caso-card-${p.slug}`}
                className="group border border-ink-500 hover:border-brand bg-ink-800 transition-colors"
              >
                <div className="aspect-[16/10] overflow-hidden bg-ink-700">
                  <img src={resolveImg(p.image_url)} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4 text-xs uppercase tracking-widest font-mono text-ash-500">
                    <span>{p.industry}</span>
                    <span>{p.year}</span>
                  </div>
                  <div className="text-brand text-xs font-mono uppercase tracking-widest mb-2">{p.client}</div>
                  <h3 className="font-display font-bold text-2xl text-ash-100 mb-3 group-hover:text-brand transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-ash-300 text-sm leading-relaxed line-clamp-2 mb-5">{p.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {p.tech_stack.slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-ink-500 text-ash-300 font-mono">{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {projects.length === 0 && (
            <div className="text-center py-24 text-ash-300">No hay proyectos para mostrar.</div>
          )}
        </div>
      </section>

      {/* CTA — agendar reunión */}
      <section className="py-20 md:py-28 border-t border-ink-500 bg-ink-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-bold text-3xl md:text-5xl text-ash-100 leading-[1.05] tracking-tight mb-6">
            ¿Quieres conocer más casos?
          </h2>
          <p className="text-ash-300 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Escríbenos y agendamos una reunión para mostrarte casos del rubro que más te interese y conversar cómo podemos ayudarte.
          </p>
          <div className="inline-flex flex-col sm:flex-row gap-3">
            <Link
              to="/contacto"
              data-testid="casos-cta-contacto"
              className="inline-flex items-center justify-center gap-3 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-8 py-4 uppercase tracking-wider text-sm"
            >
              Agendar reunión <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/cotizador"
              data-testid="casos-cta-cotizar"
              className="inline-flex items-center justify-center gap-3 border border-ink-500 hover:border-brand text-ash-100 hover:text-brand px-8 py-4 uppercase tracking-wider text-sm"
            >
              Cotizar mi proyecto
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function CasoDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(null)).finally(() => setLoading(false));
  }, [slug]);

  useSeo({
    title: project ? `${project.title} · Caso de éxito · DoSoft` : "Caso · DoSoft",
    description: project ? project.summary : "Caso de éxito desarrollado por DoSoft.",
    canonical: `https://dosoft.cl/casos/${slug}`,
    image: project?.image_url,
    jsonLd: project
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: project.title,
            alternativeHeadline: project.client,
            description: project.summary,
            inLanguage: "es-CL",
            image: project.image_url?.startsWith("http")
              ? project.image_url
              : `https://dosoft.cl${project.image_url || ""}`,
            datePublished: `${project.year || 2026}-01-01`,
            url: `https://dosoft.cl/casos/${slug}`,
            mainEntityOfPage: `https://dosoft.cl/casos/${slug}`,
            author: { "@type": "Organization", "@id": "https://dosoft.cl/#org", name: "DoSoft" },
            publisher: { "@type": "Organization", "@id": "https://dosoft.cl/#org", name: "DoSoft" },
            about: project.industry,
            keywords: [project.industry, project.client, ...(project.tech_stack || [])].join(", "),
            articleSection: "Casos de éxito",
          },
          buildBreadcrumb([
            { name: "Inicio", url: "/" },
            { name: "Casos de éxito", url: "/casos" },
            { name: project.client || project.title, url: `/casos/${slug}` },
          ]),
        ]
      : undefined,
  });

  if (loading) return <div className="py-32 text-center text-ash-300">Cargando…</div>;
  if (!project) return (
    <div className="py-32 text-center">
      <div className="text-ash-300 mb-4">Caso no encontrado</div>
      <Link to="/casos" className="text-brand uppercase tracking-widest text-sm">← Volver a casos</Link>
    </div>
  );

  return (
    <div data-testid="caso-detail-page">
      <section className="relative border-b border-ink-500 overflow-hidden">
        <div className="absolute inset-0">
          <img src={resolveImg(project.image_url)} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-800 via-ink-800/70 to-ink-800/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32">
          <Link to="/casos" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ash-300 hover:text-brand mb-8">
            <ArrowLeft size={14} /> Volver a casos
          </Link>
          <div className="text-brand text-xs uppercase tracking-widest font-mono mb-4">
            {project.industry}
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-ash-100 max-w-4xl leading-[0.95]">
            {project.title}
          </h1>
          <p className="text-lg text-ash-300 mt-8 max-w-3xl leading-relaxed">{project.summary}</p>
          {project.live_url && (
            <a
              href={project.live_url}
              target="_blank"
              rel="noreferrer"
              data-testid="caso-live-cta"
              className="inline-flex items-center gap-3 mt-10 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-6 py-3 uppercase tracking-wider text-xs"
            >
              {project.live_url_label || "Ver plataforma en vivo"} <ExternalLink size={14} />
            </a>
          )}
        </div>
      </section>

      <section className="py-16 md:py-24 border-b border-ink-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-4 gap-px bg-ink-500 border border-ink-500">
          {project.results.map((r, i) => (
            <div key={i} className="bg-ink-800 p-8">
              <TrendingUp size={20} strokeWidth={1.5} className="text-brand mb-4" />
              <div className="font-display font-bold text-2xl text-ash-100 leading-tight">{r}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24 border-b border-ink-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">Desafío</div>
            <h2 className="font-display font-bold text-3xl text-ash-100 mb-6">El contexto.</h2>
            <p className="text-ash-300 leading-relaxed text-lg">{project.challenge}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">Solución</div>
            <h2 className="font-display font-bold text-3xl text-ash-100 mb-6">Cómo lo resolvimos.</h2>
            <p className="text-ash-300 leading-relaxed text-lg">{project.solution}</p>
          </div>
        </div>
      </section>

      {project.features && project.features.length > 0 && (
        <section className="py-16 md:py-24 border-b border-ink-500 bg-ink-900">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">Funcionalidades</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-ash-100 mb-10 max-w-2xl">Todo en una sola plataforma.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-500 border border-ink-500">
              {project.features.map((f, i) => (
                <div key={i} data-testid={`caso-feature-${i}`} className="bg-ink-800 p-6 md:p-7 hover:bg-ink-700 transition-colors">
                  <div className="w-9 h-9 border border-brand flex items-center justify-center mb-4">
                    <Sparkles size={14} strokeWidth={1.5} className="text-brand" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ash-100 mb-2 leading-tight">{f.title}</h3>
                  <p className="text-ash-300 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {project.gallery && project.gallery.length > 0 && (
        <section className="py-16 md:py-24 border-b border-ink-500" data-testid="caso-gallery">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">Galería</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-ash-100 mb-10 max-w-2xl">La plataforma en acción.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {project.gallery.map((src, i) => (
                <a
                  key={i}
                  href={resolveImg(src)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`caso-gallery-${i}`}
                  className="group relative block aspect-[4/3] overflow-hidden border border-ink-500 hover:border-brand transition-colors bg-ink-900"
                >
                  <img
                    src={resolveImg(src)}
                    alt={`${project.title} — imagen ${i + 1}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 border-b border-ink-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">Stack técnico</div>
          <div className="flex flex-wrap gap-3">
            {project.tech_stack.map((t) => (
              <span key={t} className="px-4 py-2 border border-ink-500 text-ash-100 font-mono text-sm">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-ash-100 mb-6">¿Tienes un desafío similar?</h2>
          <div className="inline-flex flex-col sm:flex-row gap-3">
            <Link to="/cotizador" className="inline-flex items-center justify-center gap-3 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-8 py-4 uppercase tracking-wider text-sm">
              Cotizar mi proyecto <ArrowUpRight size={16} />
            </Link>
            {project.live_url && (
              <a
                href={project.live_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-3 border border-ink-500 hover:border-brand text-ash-100 hover:text-brand px-8 py-4 uppercase tracking-wider text-sm"
              >
                {project.live_url_label || "Visitar plataforma"} <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
